import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useRelativeTime } from '@/hooks/useRelativeTime'
import FigmaIcon from '@/assets/icons/icon-figma.svg?react'
import css from './FigmaLightbox.module.css'

interface Props {
    imageUrl: string
    frameUrl: string
    frameName: string | null
    fileName: string | null
    lastModified: string | null
    onClose: () => void
}

const FigmaLightbox = ({ imageUrl, frameUrl, frameName, fileName, lastModified, onClose }: Props) => {
    const [zoom, setZoom] = useState(1)
    const formatRelativeTime = useRelativeTime()

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                onClose()
            }
        }
        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [onClose])

    const title = frameName || fileName || 'Figma'
    const subtitle = [fileName, frameName].filter(Boolean).join(' · ')

    return createPortal(
        <div className={css.overlay} data-figma-lightbox="true" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
            <div className={css.container}>
                <div className={css.header}>
                    <span className={css.title}>{title}</span>
                    <button className={css.close} onClick={onClose}>×</button>
                </div>
                <div className={css.body}>
                    <img
                        src={imageUrl}
                        alt={title}
                        className={css.image}
                        style={{ transform: `scale(${zoom})` }}
                        draggable={false}
                    />
                    <div className={css.zoom_controls}>
                        <button className={css.zoom_btn} onClick={() => setZoom(z => Math.min(z + 0.25, 4))}>+</button>
                        <button className={css.zoom_btn} onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))}>−</button>
                    </div>
                </div>
                <div className={css.footer}>
                    <div className={css.footer_left}>
                        <FigmaIcon className={css.footer_icon} />
                        <span className={css.footer_info}>{subtitle || 'Figma'}</span>
                    </div>
                    <div className={css.footer_right}>
                        {lastModified && <span className={css.footer_time}>Edited {formatRelativeTime({ toDate: () => new Date(lastModified) })}</span>}
                        <a href={frameUrl} target="_blank" rel="noopener noreferrer" className={css.open_link}>
                            Open in Figma
                        </a>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default FigmaLightbox
